import CustomModal from 'components/DesignSystems/New/CustomModal';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import {
  useActualIncidentId,
  useGroupGuid,
} from 'components/IAP/hooks/commonHooks';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { useGetIncidentPositionInfo } from 'components/IncidentConfig/hooks/useGetIncidentPositionInfo';
import { useUpdatePositionAndRole } from 'components/IncidentConfig/hooks/useUpdatePositionAndRole';
import { GetIncidentPositionResponse } from 'components/IncidentConfig/incidentConfig.types';
import MemberSelectTable from 'components/IncidentConfig/MemberSelectTable';
import PositionAssignee from 'components/IncidentConfig/PositionAssignee';
import EditRolePermissions, {
  PermissionOption,
} from 'components/Roles/EditRolePermissions';
import { useGetPermissions } from 'components/Roles/hooks/useGetPermissions';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  useAddUsersToIncidentRoster,
  useGetRostersForGroup,
} from 'hooks/incident/useRoster';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AssignNewExternalUserForm from './AssignNewExternalUserForm';

const INCIDENT_PERMISSION_ACTIONS = [
  'incident:changeRoster',
  'incident:proposeRosterChange',
  'incident:approveRosterChange',
  'incident:activateMember',
  'incident:activateSelf',
  'incident:checkInSelf',
  'incident:editCards',
  'incident:generateIap',
  'incident:advanceOperationalPeriod',
  'incident:editSettings',
  'incident:accessAi',
  'incident:viewApprovedMaterials',
  'incident:viewFuturePeriod',
  'incident:viewSharedChats',
  'incident:roster:change',
  'incident:roster:propose',
  'incident:roster:approve',
  'resourceSet:edit',
  'resourceRequest:create',
  'internalLayer:create',
  'internalLayer:propose',
  'internalLayer:approve',
  // Hiding until draft notifications are implemented
  // 'notification:approve',
  // 'notification:propose',
  'sitrep:create',
  'sitrep:draft',
  'sitrep:approve',
  'notification:create',
];

const EditPositionModalHeading: React.FC<{
  positionName: string;
  isTemplatePosition: boolean;
  updatePositionName: (newName: string) => void;
}> = ({ positionName, isTemplatePosition, updatePositionName }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(positionName);

  useEffect(() => {
    setTempName(positionName);
  }, [positionName]);

  return (
    <div className="d-flex align-items-center gap-2">
      {!isEditing ? (
        <div className="d-flex align-items-center gap-2">
          <span>{positionName}</span>
          {isTemplatePosition ? (
            <SharedIcon
              iconName="info"
              size="sm"
              color="primary"
              title={'You cannot edit the name of a template position'}
              aria-label={'You cannot edit the name of a template position'}
              style={{ cursor: 'default' }}
            />
          ) : (
            <StylishNewButton
              className="button--icon"
              style={{ padding: '0', border: 'none' }}
              onClick={() => setIsEditing(true)}
            >
              <SharedIcon
                iconName="edit"
                size="sm"
                color="primary"
                title={'Edit position name'}
                aria-label={'Edit position name'}
              />
            </StylishNewButton>
          )}
        </div>
      ) : (
        <div className="d-flex align-items-center gap-2">
          <StylishNewInput
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            autoFocus
            style={{ width: '40vw' }}
          />
          <StylishNewButton
            className="button--icon"
            style={{ padding: '0', border: 'none' }}
            onClick={() => {
              setIsEditing(false);
              updatePositionName(tempName);
            }}
          >
            <SharedIcon
              iconName="check"
              size="sm"
              color="primary"
              title={'Save position name'}
              aria-label={'Save position name'}
            />
          </StylishNewButton>
          <StylishNewButton
            className="button--icon"
            style={{ padding: '0', border: 'none' }}
            onClick={() => {
              setTempName(positionName);
              setIsEditing(false);
            }}
          >
            <SharedIcon
              iconName="close"
              size="sm"
              color="primary"
              title={'Cancel edit'}
              aria-label={'Cancel edit'}
            />
          </StylishNewButton>
        </div>
      )}
    </div>
  );
};

const EditPositionModal: React.FC<{
  show: boolean;
  onHide: () => void;
  positionId: string;
  incidentMembers: RosterEntry[];
}> = ({ show, onHide, positionId, incidentMembers }) => {
  const resolvedIncidentId = useActualIncidentId();
  const resolvedGroupId = useGroupGuid();
  const [positionName, setPositionName] = useState('');
  const [newPermissions, setPositionPermissions] = useState<PermissionOption[]>(
    []
  );
  const [isAssigningLeader, setIsAssigningLeader] = useState(false);
  const [isAssigningUsers, setIsAssigningUsers] = useState(false);

  const [isAssigningRemoteLeader, setIsAssigningRemoteLeader] = useState(false);
  const [isAssigningRemoteUsers, setIsAssigningRemoteUsers] = useState(false);

  const updatePositionFromQueries = useCallback(
    (posInfo: GetIncidentPositionResponse) => {
      setPositionName(posInfo.name);
      setPositionPermissions(posInfo.permissions || []);
    },
    [setPositionName, setPositionPermissions]
  );

  const membersAssignedToPosition = useMemo(() => {
    return incidentMembers
      .filter((member) =>
        member.incidentPositions?.some((pos) => pos.positionId === positionId)
      )
      .map((member) => ({
        ...member,
        isLeader:
          member.incidentPositions?.find((pos) => pos.positionId === positionId)
            ?.isLeader || false,
      }));
  }, [incidentMembers, positionId]);

  const {
    data: positionInfo = {} as GetIncidentPositionResponse,
    isLoading: isLoadingPositionInfo,
    isError: isErrorPositionInfo,
  } = useGetIncidentPositionInfo(
    resolvedIncidentId,
    positionId,
    updatePositionFromQueries
  );

  const {
    data: allPermissions = [],
    isLoading: isLoadingPermissions,
    isError: isErrorPermissions,
  } = useGetPermissions();

  const rostersForGroupQuery = useGetRostersForGroup(resolvedGroupId);

  const updatePositionAndRoleMutation = useUpdatePositionAndRole();

  const addUsersToIncident = useAddUsersToIncidentRoster(
    resolvedIncidentId,
    resolvedGroupId
  );

  const incidentPermissions = useMemo(() => {
    return allPermissions.filter((perm) =>
      INCIDENT_PERMISSION_ACTIONS.includes(perm.action)
    );
  }, [allPermissions]);

  const positionLeader = useMemo(() => {
    return membersAssignedToPosition.find((member) => member.isLeader);
  }, [membersAssignedToPosition]);

  const positionAssignees = useMemo(() => {
    return membersAssignedToPosition.filter((member) => !member.isLeader);
  }, [membersAssignedToPosition]);

  const handlePermissionChange = useCallback(
    (newPermissions: PermissionOption[]) => {
      setPositionPermissions(newPermissions);
      updatePositionAndRoleMutation.mutate({
        incidentId: resolvedIncidentId,
        position: {
          id: positionId,
          name: positionName,
        },
        permissions: newPermissions.map((perm) => ({ id: perm.id })),
      });
    },
    [
      resolvedIncidentId,
      positionId,
      positionName,
      updatePositionAndRoleMutation,
    ]
  );

  const handleNameChange = useCallback(
    (newName: string) => {
      setPositionName(newName);
      updatePositionAndRoleMutation.mutate({
        incidentId: resolvedIncidentId,
        position: {
          id: positionId,
          name: newName,
        },
        permissions: newPermissions.map((perm) => ({ id: perm.id })),
      });
    },
    [
      resolvedIncidentId,
      positionId,
      newPermissions,
      updatePositionAndRoleMutation,
    ]
  );

  const modalHeading = useMemo(() => {
    return (
      <EditPositionModalHeading
        positionName={positionName}
        isTemplatePosition={positionInfo.isTemplate}
        updatePositionName={handleNameChange}
      />
    );
  }, [positionName, positionInfo.isTemplate, handleNameChange]);

  return isLoadingPositionInfo || isLoadingPermissions ? (
    <div>Loading...</div>
  ) : isErrorPositionInfo || isErrorPermissions ? (
    <div>Error loading position info</div>
  ) : (
    <CustomModal
      heading={modalHeading}
      show={show}
      onClose={onHide}
      modalStyle={{ '--bs-modal-width': '50vw' } as React.CSSProperties}
    >
      {positionInfo.isWorkgroup && (
        <div>
          <div
            className={
              'd-flex flex-row justify-content-between align-items-center width-100'
            }
          >
            <h5 className="m-0">Leader</h5>
            <div className="d-flex flex-row justify-content-between align-items-center">
              {positionLeader && (
                <SharedIcon
                  iconName="info"
                  size="sm"
                  color="primary"
                  title={
                    'You cannot assign more than one leader. Please delete the existing leader before assigning a new one.'
                  }
                  aria-label={
                    'You cannot assign more than one leader. Please delete the existing leader before assigning a new one.'
                  }
                  style={{ cursor: 'default' }}
                />
              )}
              <StylishNewButton
                onClick={() => {
                  setIsAssigningLeader(false);
                  setIsAssigningRemoteLeader(!isAssigningRemoteLeader);
                  setIsAssigningUsers(false);
                  setIsAssigningRemoteUsers(false);
                }}
                className="ms-2 button--secondary"
                disabled={positionLeader ? true : false}
              >
                + Assign New External Leader
              </StylishNewButton>
              <StylishNewButton
                className="ms-2"
                onClick={() => {
                  setIsAssigningLeader(!isAssigningLeader);
                  setIsAssigningRemoteLeader(false);
                  setIsAssigningUsers(false);
                  setIsAssigningRemoteUsers(false);
                }}
                disabled={positionLeader ? true : false}
              >
                + Assign Leader
              </StylishNewButton>
            </div>
          </div>
          <hr className="mt-2 mb-4" />
        </div>
      )}
      {isAssigningLeader && (
        <MemberSelectTable
          groupRoster={rostersForGroupQuery}
          currentPositionMembers={membersAssignedToPosition}
          onSelectMembers={(selectedMembers) =>
            addUsersToIncident.mutate({
              positionAssignments: selectedMembers.map((member) => ({
                positionId: positionId,
                positionName: positionName,
                incidentMemberUserId: member.user_guid,
                isLeader: true,
              })),
            })
          }
          closeTable={() => setIsAssigningLeader(false)}
          selectingLeader={true}
        />
      )}
      {isAssigningRemoteLeader && (
        <AssignNewExternalUserForm
          incidentId={resolvedIncidentId}
          positionId={positionId}
          positionName={positionName}
          selectingLeader={true}
          closeTable={() => setIsAssigningRemoteLeader(false)}
        />
      )}

      {positionLeader ? (
        <div className="mb-4">
          <PositionAssignee
            incidentId={resolvedIncidentId}
            positionId={positionId}
            positionMember={positionLeader}
          />
        </div>
      ) : null}
      <div>
        <div
          className={
            'd-flex flex-row justify-content-between align-items-center width-100'
          }
        >
          <h5 className="m-0">Assignees</h5>
          <div className="d-flex flex-row justify-content-between align-items-center">
            <StylishNewButton
              onClick={() => {
                setIsAssigningLeader(false);
                setIsAssigningRemoteLeader(false);
                setIsAssigningUsers(false);
                setIsAssigningRemoteUsers(!isAssigningRemoteUsers);
              }}
              className="ms-2 button--secondary"
            >
              + Assign New External User
            </StylishNewButton>
            <StylishNewButton
              className="ms-2"
              onClick={() => {
                setIsAssigningLeader(false);
                setIsAssigningRemoteLeader(false);
                setIsAssigningUsers(!isAssigningUsers);
                setIsAssigningRemoteUsers(false);
              }}
            >
              + Assign User
            </StylishNewButton>
          </div>
        </div>
        <hr className="mt-2 mb-4" />
      </div>
      {isAssigningRemoteUsers && (
        <AssignNewExternalUserForm
          incidentId={resolvedIncidentId}
          positionId={positionId}
          positionName={positionName}
          selectingLeader={false}
          closeTable={() => setIsAssigningRemoteUsers(false)}
        />
      )}
      {isAssigningUsers && (
        <MemberSelectTable
          groupRoster={rostersForGroupQuery}
          currentPositionMembers={membersAssignedToPosition}
          selectingLeader={false}
          onSelectMembers={(selectedMembers) =>
            addUsersToIncident.mutate({
              positionAssignments: selectedMembers.map((member) => ({
                positionId: positionId,
                positionName: positionName,
                incidentMemberUserId: member.user_guid,
                isLeader: false,
              })),
            })
          }
          closeTable={() => setIsAssigningUsers(false)}
        />
      )}
      {positionAssignees.map((assignee, index) => (
        <div key={assignee.incidentMemberId}>
          <PositionAssignee
            incidentId={resolvedIncidentId}
            positionId={positionId}
            positionMember={assignee}
          />
          {index < positionAssignees.length - 1 && (
            <hr className="opacity-50 mt-4 mb-4" />
          )}
        </div>
      ))}
      <EditRolePermissions
        permissionsOptions={incidentPermissions}
        currentPermissions={newPermissions}
        onPermissionChange={handlePermissionChange}
      />
    </CustomModal>
  );
};

export default EditPositionModal;
